import {
  InternalUser,
  CreateInternalUserRequest,
  UpdateInternalUserRequest,
  InternalUserFilters,
  InternalUserRole,
  PaginatedInternalUsers,
} from '@/types';
import { HttpClient, ApiResponse } from './httpClient';
import { env, isProd } from '@/config/env';
import { withRetry } from '@/utils/withRetry';

interface DeactivateUsersResult {
  deactivatedUserIds: string[];
  failedUserIds: { userId: string; reason: string }[];
  totalRequested: number;
  totalDeactivated: number;
  totalFailed: number;
}

// Validate environment configuration - env.userServiceBaseUrl already resolves from
// globalThis.__env__ (runtime) or import.meta.env (dev), with a localhost default.
// In any deployed environment (isProd=true in all built images), a localhost fallback
// is not acceptable - the URL must be explicitly configured via ConfigMap.
if (isProd && env.userServiceBaseUrl.includes('localhost')) {
  throw new Error('VITE_USER_SERVICE_BASE_URL must be explicitly configured in deployed environments');
}

const EFFECTIVE_BASE_URL = env.userServiceBaseUrl;

/**
 * Service class for managing internal users
 */
class InternalUserService {
  private static readonly GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = EFFECTIVE_BASE_URL;
  }

  /**
   * Unwrap an API response and throw if no data is present
   */
  private unwrapUser(response: ApiResponse<InternalUser>, action: string): InternalUser {
    const user = (response as any).data || response;
    if (!user) {
      throw new Error(`Failed to ${action} user: No data returned from API`);
    }
    return user as InternalUser;
  }

  /**
   * Wrapper for HttpClient.fetchWithToken that prefixes the User Service base URL.
   * Returns ApiResponse<T> directly from the HTTP client without type assertions.
   * @param endpoint - API endpoint path (will be prefixed with baseUrl)
   * @param options - Fetch request options
   * @returns Promise resolving to ApiResponse<T> containing the response data
   */
  private async fetchWithAuth<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    return HttpClient.fetchWithToken<T>(`${this.baseUrl}${endpoint}`, options);
  }

  /**
   * Maps a raw area item (state/district) from the API to an id/name object or string.
   * Returns an object with id/name if available, otherwise a plain ID string.
   */
  private mapAreaItem(item: any): string | { id: string; name: string } {
    if (typeof item === 'object' && item) {
      const id = item.id || item.stateId || item.districtId || item.Id;
      const name = item.name || item.Name;
      if (name) {
        return { id: id || String(item), name };
      }
      // If no name, return just the ID string so locationService can resolve it
      return id || String(item);
    }
    // If it's a GUID string, return it as-is for locationService to resolve
    if (typeof item === 'string') {
      return item;
    }
    return String(item);
  }

  /**
   * Maps a raw API user object to the InternalUser interface.
   */
  private mapApiUser(apiUser: any): InternalUser {
    const groups = apiUser.groups || [];
    const organization = groups.length > 0 ? groups[0] : '';

    const userRoles = apiUser.userRoles || [];
    const role = userRoles.length > 0 ? userRoles[0] : '';

    return {
      id: apiUser.id,
      name: apiUser.name || '',
      // COMP-1712: pass through the API's separate name fields so Edit
      // dialogs don't have to split `name` on space and mis-route user
      // additions (e.g., "Test" → "Test ABC" would resurface as
      // firstName="Test", lastName="ABC User" on next open).
      // Copilot (S1): only map non-empty strings. `null` and `""` would
      // violate the `string | undefined` type on InternalUser AND block the
      // Edit dialog's split-on-space fallback when the API returns the
      // fields present-but-empty for legacy records.
      ...(typeof apiUser.firstName === 'string' && apiUser.firstName.trim() !== ''
        ? { firstName: apiUser.firstName }
        : {}),
      ...(typeof apiUser.lastName === 'string' && apiUser.lastName.trim() !== ''
        ? { lastName: apiUser.lastName }
        : {}),
      email: apiUser.email || '',
      mobile: apiUser.mobileNumber || apiUser.mobile || '',
      organization,
      groups,
      role,
      // Kept in the exact casing the API returns (e.g. "Administrator", "User") so it matches
      // the `id` values from GET /Roles?userType=Internal one-for-one — the Edit dialog's
      // Select relies on this for its initial value to match a MenuItem.
      roleId: role,
      ...(apiUser.serviceProviderId ? { serviceProviderId: apiUser.serviceProviderId } : {}),
      status: this.mapApiStatusToStatus(apiUser.isActive, apiUser.isActivated, apiUser.invitationStatus),
      // COMP-1506: preserve the raw invitationStatus so the IUM grid can
      // render the Revoked icon on deactivated rows. Coarse `status`
      // above still drives filters, dialogs, and row styling.
      ...(typeof apiUser.invitationStatus === 'string' && apiUser.invitationStatus !== ''
        ? { invitationStatus: apiUser.invitationStatus }
        : {}),
      isActive: apiUser.isActive !== undefined ? apiUser.isActive : true,
      isActivated: apiUser.isActivated,
      assignedAreas: {
        states: (apiUser.authorizedStates || []).map((item: any) => this.mapAreaItem(item)),
        dispatchAreas: (apiUser.authorizedDispatchAreas || []).map((item: any) => this.mapAreaItem(item)),
        districts: (apiUser.authorizedDistricts || []).map((item: any) => this.mapAreaItem(item))
      },
      createdAt: apiUser.createdAt || new Date().toISOString(),
      updatedAt: apiUser.updatedAt || new Date().toISOString(),
      lastLogin: apiUser.lastLoginAt,
      userType: apiUser.userType,
      dateOfBirth: apiUser.dateOfBirth,
      hireDate: apiUser.hireDate
    };
  }

  /**
   * Fetch Internal users only (userType=Internal), paginated on the server.
   * page is 0-based (MUI DataGrid convention); the server expects 1-based pageNumber.
   */
  async fetchUsers(
    filters: InternalUserFilters = {},
    page: number = 0,
    pageSize: number = 100
  ): Promise<PaginatedInternalUsers> {
    const paged = await HttpClient.fetchUserPage(
      this.fetchWithAuth.bind(this),
      'Internal',
      page,
      pageSize,
      {
        search: filters.search,
        isActive: filters.isActive,
        roles: filters.roles,
        organizations: filters.organizations,
        serviceProviderIds: filters.serviceProviderIds,
        assignedStateIds: filters.assignedStateIds,
        includeUnassignedAreas: filters.includeUnassignedAreas,
      }
    );
    const mappedUsers: InternalUser[] = paged.items.map((apiUser) => this.mapApiUser(apiUser));

    return {
      users: mappedUsers,
      total: paged.totalCount,
      page: paged.pageNumber - 1,
      pageSize: paged.pageSize,
      totalPages: paged.totalPages,
    };
  }

  /**
   * Fetch internal users by allowed roles and approving organization groups.
   */
  async fetchInternalUsersByRoleAndOrg(orgs: string[], roles: string[]): Promise<InternalUser[]> {
    const normalizedOrgs = orgs
      .map((org) => org?.trim())
      .filter((org): org is string => Boolean(org));
    const normalizedRoles = roles
      .map((role) => role?.trim())
      .filter((role): role is string => Boolean(role));

    if (normalizedOrgs.length === 0 || normalizedRoles.length === 0) {
      return [];
    }

    const query = new URLSearchParams();
    normalizedOrgs.forEach((org) => query.append('orgs', org));
    normalizedRoles.forEach((role) => query.append('roles', role));

    // Wrapped in withRetry to absorb transient network/5xx blips (e.g. when the
    // single user-service pod is cold-starting or restarting) so callers don't
    // fail on a momentary gateway error. Applies to every caller of this method.
    const usersResponse = await withRetry(() =>
      this.fetchWithAuth<InternalUser[]>(
        `/Users/internal/by-role-and-org?${query.toString()}`,
        { method: 'GET' }
      )
    );

    const users = Array.isArray(usersResponse) ? usersResponse : (usersResponse.data || []);
    return users.map((apiUser: any) => this.mapApiUser(apiUser));
  }

  /**
   * Map API status fields to our status type
   * Uses isActive and isActivated to determine user status
   */
  private mapApiStatusToStatus(
    isActive: boolean | undefined,
    isActivated: number | undefined,
    invitationStatus: string | undefined
  ): 'Active' | 'Inactive' | 'Pending' {
    // If isActive is false, the user is Inactive (deactivated)
    if (isActive === false) {
      return 'Inactive';
    }
    
    // If isActivated is 2, the user is Deactivated
    if (isActivated === 2) {
      return 'Inactive';
    }
    
    // If isActivated is 0, the user is NotActivated (pending)
    if (isActivated === 0) {
      return 'Pending';
    }
    
    // Check invitation status as fallback
    if (invitationStatus) {
      switch (invitationStatus) {
        case 'Invited':
        case 'InProgress':
        case 'NotStarted':
          return 'Pending';
        case 'InvitationExpired':
          return 'Inactive';
      }
    }
    
    // Default to Active if isActive is true or undefined
    return 'Active';
  }

  /**
   * Normalize array of IDs to lowercase strings for API
   * Filters out empty or invalid GUID values
   */
  private normalizeIds(ids: string[]): string[] {
    return ids
      .filter(id => {
        if (!id || typeof id !== 'string') return false;
        return InternalUserService.GUID_REGEX.test(id.trim());
      })
      .map(id => id.trim().toLowerCase());
  }

  /**
   * Create a new internal user
   */
  async createUser(userData: CreateInternalUserRequest): Promise<InternalUser> {
    // userData.userRoles are already exact-cased enum names (e.g. "Administrator", "User"),
    // sourced from GET /Roles?userType=Internal — no client-side casing translation needed.
    const mappedRoles = userData.userRoles;

    // Normalize and filter GUID arrays
    const normalizedStates = userData.authorizedStates ? this.normalizeIds(userData.authorizedStates) : [];
    const normalizedDistricts = userData.authorizedDistricts ? this.normalizeIds(userData.authorizedDistricts) : [];
    const normalizedDispatchAreas = userData.authorizedDispatchAreas ? this.normalizeIds(userData.authorizedDispatchAreas) : [];
    
    // Build API payload matching CreateUserCommand structure
    const apiPayload: any = {
      firstName: userData.firstName,
      lastName: userData.lastName,
      name: userData.name,
      email: userData.email,
      ...(userData.mobileNumber ? { mobileNumber: userData.mobileNumber } : {}),
      userType: "Internal",
      userRoles: mappedRoles,
      dateOfBirth: "1990-01-01T00:00:00Z",
      hireDate: new Date().toISOString().split('T')[0] + "T00:00:00Z",
      groups: userData.groups || [],
    };

    // Only add serviceProviderId if it's a valid GUID
    if (userData.serviceProviderId && InternalUserService.GUID_REGEX.test(userData.serviceProviderId)) {
      apiPayload.serviceProviderId = userData.serviceProviderId.toLowerCase();
    }

    // Only include authorization arrays if they have valid GUIDs
    if (normalizedStates.length > 0) {
      apiPayload.authorizedStates = normalizedStates;
    }
    if (normalizedDispatchAreas.length > 0) {
      apiPayload.authorizedDispatchAreas = normalizedDispatchAreas;
    }
    if (normalizedDistricts.length > 0) {
      apiPayload.authorizedDistricts = normalizedDistricts;
    }

    const response = await this.fetchWithAuth<InternalUser>(
      '/Users',
      {
        method: 'POST',
        body: JSON.stringify(apiPayload),
      }
    );

    return this.unwrapUser(response, 'create');
  }

  /**
   * Update an existing internal user
   */
  async updateUser(userId: string, userData: UpdateInternalUserRequest): Promise<InternalUser> {
    const response = await this.fetchWithAuth<InternalUser>(
      `/Users/${userId}`,
      {
        method: 'PUT',
        body: JSON.stringify(userData),
      }
    );

    return this.unwrapUser(response, 'update');
  }

  /**
   * Delete an internal user
   */
  async deleteUser(userId: string): Promise<void> {
    await this.fetchWithAuth<void>(`/Users/${userId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<InternalUser> {
    const response = await this.fetchWithAuth<InternalUser>(
      `/Users/${userId}`
    );

    // Handle both wrapped and direct response formats
    const apiUser: any = response.data || response;

    if (!apiUser?.id) {
      throw new Error(`Failed to get user: No data returned for user ID ${userId}`);
    }

    return this.mapApiUser(apiUser);
  }


  /**
   * Fetch the roles assignable to internal users from the authoritative
   * user-service endpoint (already excludes deprecated roles server-side).
   */
  async getAssignableRoles(): Promise<InternalUserRole[]> {
    // Wrapped in withRetry to absorb transient network/5xx blips (e.g. when the
    // single user-service pod is cold-starting or restarting) — this list drives
    // the Add/Edit role dropdown, so a momentary gateway error shouldn't strand it.
    const response = await withRetry(() =>
      this.fetchWithAuth<InternalUserRole[]>(
        '/Roles?userType=Internal',
        { method: 'GET' }
      )
    );
    const data = response.data || response;
    return Array.isArray(data) ? data : [];
  }

  /**
   * Deactivate users (single or multiple)
   */
  async deactivateUsers(userIds: string[]): Promise<DeactivateUsersResult> {
    const response = await this.fetchWithAuth<DeactivateUsersResult>(
      '/Users/deactivate',
      {
        method: 'POST',
        body: JSON.stringify({ userIds }),
      }
    );
    // API returns data directly, not wrapped in ApiResponse
    return (response.data || response) as DeactivateUsersResult;
  }

  /**
   * Deactivate single user
   */
  async deactivateUser(userId: string): Promise<void> {
    await this.deactivateUsers([userId]);
  }

  /**
   * Resend invitation to user
   */
  async resendInvitation(userId: string): Promise<void> {
    await this.fetchWithAuth<void>(
      `/Users/${userId}/invite/resend`,
      { method: 'POST' }
    );
  }

  /**
   * Bulk delete users
   */
  async bulkDeleteUsers(userIds: string[]): Promise<void> {
    await this.fetchWithAuth<void>(
      '/Users/bulk-delete',
      {
        method: 'DELETE',
        body: JSON.stringify({ userIds }),
      }
    );
  }
}

// Export singleton instance
export const internalUserService = new InternalUserService();
export default internalUserService;
